import React from "react";
import { Typography, Box, Chip, Paper, useTheme, alpha } from "@mui/material";
import { translate } from "../utils/currency";
import { useMainStore } from "../store/store";

type IMerchants = {
  rates: {
    payment_method: string;
    operation_type: string;
    currency: string;
    rate: number;
    merchant_id: number;
    minsum: number;
    maxsum: number;
  }[];
};

const MerchantCard: React.FC<IMerchants> = ({ rates }) => {
  const theme = useTheme();
  const { curr } = useMainStore();

  const merchantRates =
    rates?.filter((el) => el.currency.toLowerCase() === curr.toLowerCase()) ||
    [];

  /** `tint` paints the accent bar and hover border, `text` stays readable. */
  const sections = [
    {
      type: "deposit",
      title: "Deposit",
      tint: theme.palette.primary.main,
      color: theme.palette.accentText,
    },
    {
      type: "payout",
      title: "Payout",
      tint: theme.palette.success.main,
      color: theme.palette.semanticText.success,
    },
    {
      type: "cashin",
      title: "Cash-in",
      tint: theme.palette.warning.main,
      color: theme.palette.semanticText.warning,
    },
    {
      type: "withdrawal",
      title: "Withdrawal",
      tint: theme.palette.info.main,
      color: theme.palette.semanticText.info,
    },
  ];

  if (merchantRates.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {sections.map((section) => {
        const sectionRates = merchantRates.filter(
          (el) => el.operation_type === section.type,
        );
        if (sectionRates.length === 0) return null;

        return (
          <Box key={section.type}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 20,
                  borderRadius: 999,
                  backgroundColor: section.tint,
                }}
              />
              <Typography
                variant="h6"
                sx={{ fontSize: 17, letterSpacing: "-0.01em" }}
              >
                {section.title}
              </Typography>
              <Chip
                size="small"
                label={sectionRates.length}
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  color: section.color,
                  backgroundColor: alpha(section.tint, 0.12),
                }}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 1.5,
              }}
            >
              {sectionRates.map((item) => (
                <Paper
                  key={`${section.type}-${item.payment_method}-${item.minsum}-${item.maxsum}`}
                  sx={{
                    p: 2,
                    borderRadius: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    transition:
                      "transform 180ms ease, border-color 180ms ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      borderColor: alpha(section.tint, 0.45),
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 620, mb: 0.75 }}
                      noWrap
                    >
                      {translate(item.payment_method)}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        px: 1,
                        py: 0.3,
                        borderRadius: "7px",
                        fontSize: 11.5,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        color: "text.secondary",
                        fontVariantNumeric: "tabular-nums",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? alpha("#FFFFFF", 0.06)
                            : alpha("#172033", 0.05),
                      }}
                    >
                      {item.minsum.toLocaleString("en-US")} –{" "}
                      {item.maxsum.toLocaleString("en-US")}
                    </Box>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: 740,
                      letterSpacing: "-0.02em",
                      color: section.color,
                      fontVariantNumeric: "tabular-nums",
                      flexShrink: 0,
                    }}
                  >
                    {item.rate.toFixed(2)}
                    <Typography
                      component="span"
                      sx={{ fontSize: 14, fontWeight: 700, ml: 0.25 }}
                    >
                      %
                    </Typography>
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default MerchantCard;
